import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  os: string;
  browser: string;
  userAgent: string;
  device: string;
  os_version: string;
  browser_version: string;
  deviceType: string;
  orientation: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  getDeviceInfo(): DeviceInfo {
    if (!this.isBrowser) {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        os: 'server',
        browser: 'server',
        userAgent: 'server',
        device: 'server',
        os_version: 'server',
        browser_version: '',
        deviceType: 'Desktop',
        orientation: '',
      };
    }

    const ua = navigator.userAgent;
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|webOS|BlackBerry|Windows Phone/i.test(ua);
    const isTablet = /Tablet|iPad/i.test(ua) ||
                     (isMobile && window.innerWidth >= 768 && window.innerWidth <= 1024);
    const deviceType = isTablet ? 'Tablet' : isMobile ? 'Mobile' : 'Desktop';
    const os = this.getOS(ua);
    const browser = this.getBrowser(ua);

    return {
      isMobile,
      isTablet,
      isDesktop: !isMobile && !isTablet,
      os,
      browser,
      userAgent: ua,
      device: this.getDevice(ua, deviceType),
      os_version: this.getOSVersion(ua, os),
      browser_version: this.getBrowserVersion(ua, browser),
      deviceType,
      orientation: this.getOrientation(),
    };
  }

  private getOS(ua: string): string {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'MacOS';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    if (ua.includes('Linux')) return 'Linux';
    return 'Unknown';
  }

  private getBrowser(ua: string): string {
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('OPR') || ua.includes('Opera')) return 'Opera';
    if (ua.includes('CriOS') || (ua.includes('Chrome') && !ua.includes('Edg'))) return 'Chrome';
    if (ua.includes('FxiOS') || ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('CriOS')) return 'Safari';
    return 'Unknown';
  }

  private getOSVersion(ua: string, os: string): string {
    if (os === 'Windows') {
      const match = ua.match(/Windows NT ([\d.]+)/);
      return match ? `Windows ${match[1]}` : os;
    }
    if (os === 'Android') {
      const match = ua.match(/Android ([\d.]+)/);
      return match ? `Android ${match[1]}` : os;
    }
    if (os === 'iOS') {
      const match = ua.match(/OS ([\d_]+)/);
      return match ? `iOS ${match[1].replace(/_/g, '.')}` : os;
    }
    if (os === 'MacOS') {
      const match = ua.match(/Mac OS X ([\d_]+)/);
      return match ? `MacOS ${match[1].replace(/_/g, '.')}` : os;
    }
    return os;
  }

  private getBrowserVersion(ua: string, browser: string): string {
    const patterns: Record<string, RegExp> = {
      Chrome: /(?:Chrome|CriOS)\/([\d.]+)/,
      Firefox: /(?:Firefox|FxiOS)\/([\d.]+)/,
      Safari: /Version\/([\d.]+)/,
      Edge: /Edg\/([\d.]+)/,
      Opera: /(?:OPR|Opera)\/([\d.]+)/,
    };
    const match = patterns[browser]?.exec(ua);
    return match?.[1] ?? '';
  }

  private getDevice(ua: string, deviceType: string): string {
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Android/i.test(ua)) {
      const match = ua.match(/;\s*([^;)]+?)\s+Build\//i);
      if (match?.[1] && !/wv|Linux/i.test(match[1])) {
        return match[1].trim();
      }
      return 'Android';
    }
    return deviceType;
  }

  private getOrientation(): string {
    const type = window.screen?.orientation?.type;
    if (typeof type === 'string') {
      if (type.startsWith('portrait')) return 'portrait';
      if (type.startsWith('landscape')) return 'landscape';
      return type;
    }
    return window.innerHeight >= window.innerWidth ? 'portrait' : 'landscape';
  }

  isMobile(): boolean {
    return this.getDeviceInfo().isMobile;
  }

  isTablet(): boolean {
    return this.getDeviceInfo().isTablet;
  }

  isDesktop(): boolean {
    return this.getDeviceInfo().isDesktop;
  }
}
